import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from '../../../services/auth.service';
import { NgxRolesService } from 'ngx-permissions';
import { Select2OptionData } from 'ng2-select2';
import { PNotifyService } from '../../../services/pnotify.service';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { SAAS_SOURCES, SaaSSource, ParsedPassenger, groupPassengersBySharedPhone } from './passenger-sync-parsers';

export interface SyncDiffResult {
  toAdd: ParsedPassenger[];
  toDeactivate: any[];
  toUpdate: any[];
  unchanged: any[];
}

@Component({
  selector: 'app-passenger-sync',
  templateUrl: './passenger-sync.component.html',
  styleUrls: ['./passenger-sync.component.css']
})
export class PassengerSyncComponent implements OnInit {

  // Step management
  currentStep: number = 1;

  // Step 1: Selection
  clientId: any = '';
  schoolId: any = '';
  selectedSourceId: string = '';
  matchMethod: string = 'auto';
  public clientsData: Array<Select2OptionData>;
  public schoolsData: Array<Select2OptionData>;
  public sourcesData: Array<Select2OptionData>;
  public matchMethodsData: Array<Select2OptionData>;

  // Files (multiple per level)
  loadedFiles: { name: string; passengers: ParsedPassenger[] }[] = [];
  parsedPassengers: ParsedPassenger[] = [];
  parseError: string = '';

  // Step 2: Preview / Diff
  diff: SyncDiffResult = null;
  diffLoading: boolean = false;
  diffError: string = '';

  // Checkboxes for confirmation
  addChecked: boolean[] = [];
  updateChecked: boolean[] = [];
  deactivateChecked: boolean[] = [];
  selectAllAdd: boolean = true;
  selectAllUpdate: boolean = true;
  selectAllDeactivate: boolean = true;

  // Step 3: Apply
  applyLoading: boolean = false;
  applyDone: boolean = false;
  applyResult: any = null;
  applyError: string = '';
  showUnchanged: boolean = false;

  pnotify = undefined;
  saasSourcesList: SaaSSource[] = SAAS_SOURCES;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private roleService: NgxRolesService,
    private router: Router,
    pnotifyService: PNotifyService
  ) {
    this.pnotify = pnotifyService.getPNotify();
    this.initClientSelect();
    this.sourcesData = [
      { id: '', text: 'Choisir une source' },
      ...this.saasSourcesList.map(s => ({ id: s.id, text: s.name }))
    ];
    this.matchMethodsData = [
      { id: 'auto', text: 'Automatique (recommandé)' },
      { id: 'matricule', text: 'Matricule' },
      { id: 'name-phone', text: 'Nom + Téléphone' }
    ];
  }

  ngOnInit() {}

  // ========== Client / School selects ==========

  initClientSelect() {
    if (this.roleService.getRole('ROLE_CLIENT') || this.roleService.getRole('ROLE_SUPER_CLIENT')) {
      const client = this.authService.getUser();
      this.clientId = client.id;
      this.clientsData = [{ id: client.id, text: client.name }];
      this.initSchoolSelect();
    } else {
      this.http.get<any[]>('/api/client/lookup').subscribe((clients: any[]) => {
        const array1 = [{ id: '', text: 'Choisir un client' }];
        const array2 = clients.map(c => ({ id: c.id, text: c.name }));
        this.clientsData = array1.concat(array2);
      });
    }
  }

  clientChanged(e: any) {
    this.clientId = e.value;
    this.initSchoolSelect();
    this.resetFromStep(1);
  }

  initSchoolSelect() {
    const params = new HttpParams({ fromObject: { clientId: this.clientId + '' } });
    this.http.get<any[]>('/api/school/lookup', { params }).subscribe((schools: any[]) => {
      const array1 = [{ id: '', text: 'Choisir un établissement' }];
      const array2 = schools.map(s => ({ id: s.id, text: s.name }));
      this.schoolsData = array1.concat(array2);
    });
  }

  schoolChanged(e: any) {
    this.schoolId = e.value;
    this.resetFromStep(1);
  }

  sourceChanged(e: any) {
    this.selectedSourceId = e.value;
    this.matchMethod = 'auto';
    this.resetFromStep(1);
  }

  matchMethodChanged(e: any) {
    this.matchMethod = e.value;
    this.resetFromStep(1);
  }

  // ========== File handling (multiple files) ==========

  onFilesChange(event: any) {
    this.parseError = '';
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const source = this.saasSourcesList.find(s => s.id === this.selectedSourceId);
    if (!source) {
      this.parseError = 'Source non sélectionnée.';
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Skip if already loaded
      if (this.loadedFiles.find(f => f.name === file.name)) continue;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          const passengers = source.parse(sheetData);
          if (passengers.length === 0) {
            this.parseError = 'Fichier "' + file.name + '": aucun passager trouvé (vérifiez le filtre Transport).';
            return;
          }

          this.loadedFiles.push({ name: file.name, passengers: passengers });
          this.mergeAllPassengers();
        } catch (err) {
          const msg = err && (err as any).message;
          this.parseError = msg || 'Erreur lors de la lecture de "' + file.name + '".';
        }
      };
      reader.readAsBinaryString(file);
    }
    // Reset input so the same file can be re-selected if removed
    event.target.value = '';
  }

  removeFile(index: number) {
    this.loadedFiles.splice(index, 1);
    this.mergeAllPassengers();
    this.resetFromStep(1);
  }

  mergeAllPassengers() {
    // Flatten all parents from all files, then group by shared phone (union-find).
    // This merges families that share at least one phone number, even when
    // parent1/parent2 phones are swapped or only partially present across files.
    const all: ParsedPassenger[] = [];
    for (const fileEntry of this.loadedFiles) {
      for (const p of fileEntry.passengers) {
        all.push({ ...p });
      }
    }
    this.parsedPassengers = groupPassengersBySharedPhone(all);
    console.log('[sync-merge] Total parsed passengers after merge:', this.parsedPassengers.length);
  }

  // ========== Step navigation ==========

  get canGoStep2(): boolean {
    return this.clientId && this.schoolId && this.selectedSourceId && this.loadedFiles.length > 0 && this.parsedPassengers.length > 0;
  }

  goToStep2() {
    if (!this.canGoStep2) return;
    this.currentStep = 2;
    this.loadDiff();
  }

  goToStep1() {
    this.currentStep = 1;
    this.diff = null;
    this.diffError = '';
  }

  goToStep3() {
    this.currentStep = 3;
    this.applyDone = false;
    this.applyError = '';
  }

  resetFromStep(step: number) {
    if (step <= 1) {
      this.diff = null;
      this.diffError = '';
    }
    if (step <= 2) {
      this.applyDone = false;
      this.applyError = '';
    }
  }

  // ========== Step 2: Load diff ==========

  loadDiff() {
    this.diffLoading = true;
    this.diffError = '';
    this.diff = null;

    this.http.post<SyncDiffResult>('/api/passenger/sync-preview', {
      schoolId: this.schoolId,
      sourceId: this.selectedSourceId,
      matchMethod: this.matchMethod,
      passengers: this.parsedPassengers
    }).subscribe(
      (result) => {
        this.diffLoading = false;
        this.diff = result;
        this.addChecked = new Array(result.toAdd.length).fill(true);
        this.updateChecked = new Array((result.toUpdate || []).length).fill(true);
        this.deactivateChecked = new Array(result.toDeactivate.length).fill(true);
        this.selectAllAdd = true;
        this.selectAllUpdate = true;
        this.selectAllDeactivate = true;
      },
      (err) => {
        this.diffLoading = false;
        this.diffError = (err.error && err.error.message) ? err.error.message : 'Erreur lors de l\'analyse.';
      }
    );
  }

  // ========== Checkbox helpers ==========

  toggleSelectAllAdd() {
    this.selectAllAdd = !this.selectAllAdd;
    this.addChecked = this.addChecked.map(() => this.selectAllAdd);
  }

  toggleSelectAllUpdate() {
    this.selectAllUpdate = !this.selectAllUpdate;
    this.updateChecked = this.updateChecked.map(() => this.selectAllUpdate);
  }

  toggleSelectAllDeactivate() {
    this.selectAllDeactivate = !this.selectAllDeactivate;
    this.deactivateChecked = this.deactivateChecked.map(() => this.selectAllDeactivate);
  }

  get selectedAddCount(): number {
    return this.addChecked.filter(c => c).length;
  }

  get selectedUpdateCount(): number {
    return this.updateChecked.filter(c => c).length;
  }

  get selectedDeactivateCount(): number {
    return this.deactivateChecked.filter(c => c).length;
  }

  get hasSelections(): boolean {
    return this.selectedAddCount > 0 || this.selectedUpdateCount > 0 || this.selectedDeactivateCount > 0;
  }

  // ========== Student counts (sum of children, not families) ==========

  sumCount(list: any[]): number {
    return (list || []).reduce((s, p) => s + (p.count || 1), 0);
  }

  get totalParsedStudents(): number {
    return this.sumCount(this.parsedPassengers);
  }

  get totalToAddStudents(): number {
    return this.sumCount(this.diff ? this.diff.toAdd : []);
  }

  get totalToUpdateStudents(): number {
    const list = this.diff ? (this.diff.toUpdate || []) : [];
    return list.reduce((s, p) => s + (p.newData && p.newData.count ? p.newData.count : (p.count || 1)), 0);
  }

  get totalToDeactivateStudents(): number {
    return this.sumCount(this.diff ? this.diff.toDeactivate : []);
  }

  get totalUnchangedStudents(): number {
    return this.sumCount(this.diff ? this.diff.unchanged : []);
  }

  get selectedAddStudents(): number {
    return this.sumCount((this.diff ? this.diff.toAdd : []).filter((_, i) => this.addChecked[i]));
  }

  get selectedUpdateStudents(): number {
    const list = (this.diff ? (this.diff.toUpdate || []) : []).filter((_, i) => this.updateChecked[i]);
    return list.reduce((s, p) => s + (p.newData && p.newData.count ? p.newData.count : (p.count || 1)), 0);
  }

  get selectedDeactivateStudents(): number {
    return this.sumCount((this.diff ? this.diff.toDeactivate : []).filter((_, i) => this.deactivateChecked[i]));
  }

  // ========== Step 3: Apply ==========

  applySync() {
    this.applyLoading = true;
    this.applyError = '';

    const toAdd = this.diff.toAdd.filter((_, i) => this.addChecked[i]);
    const toUpdate = (this.diff.toUpdate || []).filter((_, i) => this.updateChecked[i]);
    const toDeactivateIds = this.diff.toDeactivate
      .filter((_, i) => this.deactivateChecked[i])
      .map(p => p.id);

    this.http.post<any>('/api/passenger/sync-apply', {
      schoolId: this.schoolId,
      sourceId: this.selectedSourceId,
      toAdd: toAdd,
      toUpdate: toUpdate,
      toDeactivateIds: toDeactivateIds
    }).subscribe(
      (result) => {
        this.applyLoading = false;
        this.applyDone = true;
        this.applyResult = result;
      },
      (err) => {
        this.applyLoading = false;
        this.applyError = (err.error && err.error.message) ? err.error.message : 'Erreur lors de la synchronisation.';
      }
    );
  }

  goToPassengerList() {
    this.router.navigate(['/passenger/list', 0, 5]);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/passenger/passenger-sync/passenger-sync.component.ts